import {
	BankOutlined,
	CompassOutlined,
	EditOutlined,
	EnvironmentOutlined,
	FileTextOutlined,
	GlobalOutlined,
	HistoryOutlined,
	InfoCircleOutlined,
	PlusOutlined,
	RadiusSettingOutlined,
	SettingOutlined,
	SafetyCertificateOutlined,
	LayoutOutlined,
} from '@ant-design/icons'
import {
	Button,
	Card,
	Col,
	Descriptions,
	Divider,
	Empty,
	Grid,
	Row,
	Space,
	Statistic,
	Tag,
	Typography,
	message,
	Tabs,
	Tooltip,
} from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useBranding, refreshBranding } from '../../common/branding/useBranding'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { httpGet, httpPatch, httpPost } from '../../common/http.service'
import ReactBeatLoader from '../../common/components/Spinner/ReactBeatLoader'
import { useTheme, useThemeColors } from '../../common/ThemeContext'
import OrganizationProfileUpdate from './OrganizationProfileUpdate'
import icon from './../../Assets/img/logo.jpg'
import primersoftLogo from './../../Assets/img/primersoft/primersoft-logo.svg'

const { useBreakpoint } = Grid
const { Title, Text, Paragraph } = Typography

const PAGE_BG = '#f8fafc'
const CARD_STYLE: React.CSSProperties = {
	borderRadius: 12,
	border: '1px solid #f0f0f0',
	boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03)',
	background: '#fff',
}

function OrganizationProfile() {
	const { theme, isDarkMode } = useTheme()
	const themeColors = useThemeColors()
	const { primary } = themeColors
	const isPrimersoft = theme === 'primersoft'
	const screens = useBreakpoint()
	const isMobile = !screens.md
	const [orgInfo, setOrgInfo] = useState<any>({})
	const { brandingLogoUrl } = useBranding()
	const themeFallbackLogo = isPrimersoft ? primersoftLogo : icon
	const logoSrc = brandingLogoUrl || themeFallbackLogo
	const [loading, setLoading] = useState(false)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
	const [hasOrganization, setHasOrganization] = useState(false)
	const [activeTab, setActiveTab] = useState('1')

	const CARD_STYLE: React.CSSProperties = {
		borderRadius: 12,
		border: `1px solid ${themeColors.border}`,
		boxShadow: themeColors.cardShadow,
		background: themeColors.bg,
	}


	useEffect(() => {
		fetchOrganization()
	}, [])

	const fetchOrganization = async () => {
		try {
			setLoading(true)
			const response: any = await httpGet('/api/v1/admin/org')
			if (response?.data) {
				setOrgInfo(response.data)
				setHasOrganization(true)
			} else {
				setHasOrganization(false)
			}
		} catch (error: any) {
			console.error('Failed to fetch organization:', error)
			setHasOrganization(false)
			if (error?.statusCode !== 404) {
				message.error('Failed to load organization information')
			}
		} finally {
			setLoading(false)
		}
	}

	const handleOpenCreateModal = () => {
		setModalMode('create')
		setIsModalOpen(true)
	}

	const handleOpenEditModal = () => {
		setModalMode('edit')
		setIsModalOpen(true)
	}

	const handleCloseModal = () => {
		setIsModalOpen(false)
	}

	const handleSubmit = async (values: any) => {
		try {
			setLoading(true)

			const trimmedWebsite = (values.websiteUrl ?? '').toString().trim()
			const payload = {
				organizationName: values.organizationName,
				description: values.description,
				longitude: parseFloat(values.longitude),
				latitude: parseFloat(values.latitude),
				allowCheckInDistance: parseInt(values.allowCheckInDistance, 10),
				websiteUrl: trimmedWebsite.length > 0 ? trimmedWebsite : null,
			}

			if (modalMode === 'create') {
				await httpPost('/api/v1/admin/org', payload)
				message.success('Organization created successfully')
			} else {
				await httpPatch('/api/v1/admin/org', payload)
				message.success('Organization updated successfully')
			}
			handleCloseModal()
			fetchOrganization()
			refreshBranding()
		} catch (error: any) {
			console.error(`Failed to ${modalMode} organization:`, error)
			message.error(`Failed to ${modalMode} organization`)
		} finally {
			setLoading(false)
		}
	}

	const bannerHeight = isMobile ? 220 : 280

	const tabItems = [
		{
			key: '1',
			label: (
				<span style={{ color: activeTab === '1' ? primary : themeColors.textSecondary }}>
					<FileTextOutlined /> General
				</span>
			),
			children: (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					style={{ padding: '24px 0' }}
				>
					<Space direction='vertical' size={24} style={{ width: '100%' }}>
						<Card size="small" title={<span style={{ color: themeColors.text }}>Description</span>} bordered style={CARD_STYLE}>
							<div
								style={{
									fontSize: 15,
									lineHeight: 1.6,
									color: themeColors.textSecondary,
									padding: '8px 4px',
									whiteSpace: 'pre-wrap'
								}}
							>
								{orgInfo.description || 'No description provided.'}
							</div>
						</Card>

						<Card size="small" title={<span style={{ color: themeColors.text }}>Website</span>} bordered style={CARD_STYLE}>
							<div style={{ padding: '8px 4px' }}>
								{orgInfo.websiteUrl ? (
									<a href={orgInfo.websiteUrl} target="_blank" rel="noreferrer" style={{ color: primary, fontSize: 15 }}>
										{orgInfo.websiteUrl}
									</a>
								) : <Text type="secondary" style={{ color: themeColors.textTertiary }}>Not set</Text>}
							</div>
						</Card>
					</Space>
				</motion.div>
			),
		},
		{
			key: '2',
			label: (
				<span style={{ color: activeTab === '2' ? primary : themeColors.textSecondary }}>
					<CompassOutlined /> Check-in
				</span>
			),
			children: (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					style={{ padding: '24px 0' }}
				>
					<Row gutter={[24, 24]}>
						<Col xs={24} md={14}>
							<Card size="small" title={<span style={{ color: themeColors.text }}>Location</span>} bordered style={{ ...CARD_STYLE, height: '100%' }}>
								<div style={{ padding: '16px 0' }}>
									<Row gutter={16}>
										<Col span={12}>
											<Statistic title={<span style={{ color: themeColors.textTertiary }}>LATITUDE</span>} value={orgInfo.latitude || '0.00'} precision={4} valueStyle={{ fontSize: 18, color: themeColors.text }} />
										</Col>
										<Col span={12}>
											<Statistic title={<span style={{ color: themeColors.textTertiary }}>LONGITUDE</span>} value={orgInfo.longitude || '0.00'} precision={4} valueStyle={{ fontSize: 18, color: themeColors.text }} />
										</Col>
									</Row>
									<Divider dashed style={{ margin: '16px 0', borderColor: themeColors.border }} />
									<div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
										<EnvironmentOutlined style={{ fontSize: 20, color: primary, marginTop: 4 }} />
										<Text type="secondary" style={{ fontSize: 13, color: themeColors.textSecondary }}>
											Staff must be within the defined radius to check in.
										</Text>
									</div>
								</div>
							</Card>
						</Col>
						<Col xs={24} md={10}>
							<Card size="small" title={<span style={{ color: themeColors.text }}>Radius</span>} bordered style={{ ...CARD_STYLE, height: '100%' }}>
								<div style={{ textAlign: 'center', padding: '24px 0' }}>
									<RadiusSettingOutlined style={{ fontSize: 32, color: primary, marginBottom: 12 }} />
									<Title level={2} style={{ margin: 0, color: primary }}>{orgInfo.allowCheckInDistance}</Title>
									<Text type="secondary" style={{ color: themeColors.textSecondary }}>meters</Text>
								</div>
							</Card>
						</Col>
					</Row>
				</motion.div>
			),
		},
		{
			key: '3',
			label: (
				<span style={{ color: activeTab === '3' ? primary : themeColors.textSecondary }}>
					<LayoutOutlined /> Branding
				</span>
			),
			children: (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					style={{ padding: '24px 0' }}
				>
					<Row gutter={[24, 24]}>
						<Col xs={24} md={12}>
							<Card size="small" title={<span style={{ color: themeColors.text }}>Logo</span>} bordered style={CARD_STYLE}>
								<div style={{
									height: 160,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									background: isDarkMode ? themeColors.subBg : '#fafafa',
									borderRadius: 8,
									padding: 16
								}}>
									<img
										src={logoSrc}
										alt='Logo'
										style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
									/>
								</div>
							</Card>
						</Col>
						<Col xs={24} md={12}>
							<Card size="small" title={<span style={{ color: themeColors.text }}>Banner</span>} bordered style={CARD_STYLE}>
								<div style={{
									height: 160,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									background: isDarkMode ? themeColors.subBg : '#fafafa',
									borderRadius: 8,
									overflow: 'hidden'
								}}>
									{orgInfo.photoUrl ? (
										<img
											src={orgInfo.photoUrl}
											alt='Banner'
											style={{ width: '100%', height: '100%', objectFit: 'cover' }}
										/>
									) : (
										<Empty description={<span style={{ color: themeColors.textTertiary }}>No banner</span>} image={Empty.PRESENTED_IMAGE_SIMPLE} />
									)}
								</div>
							</Card>
						</Col>
					</Row>
				</motion.div>
			),
		}
	]

	return (
		<>
			<GlobalHeader />

			{loading ? (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '80vh',
						background: themeColors.chartBg,
					}}
				>
					<ReactBeatLoader />
				</div>
			) : (
				<div style={{ background: themeColors.chartBg, minHeight: 'calc(100vh - 64px)' }}>
					{/* Hero Section */}

					<div
						style={{
							position: 'relative',
							width: '100%',
							height: bannerHeight,
							overflow: 'hidden',
						}}
					>
						{/* Background */}
						<div
							style={{
								position: 'absolute',
								inset: 0,
								background: orgInfo.photoUrl
									? `url(${orgInfo.photoUrl}) center/cover no-repeat`
									: `linear-gradient(135deg, ${primary} 0%, #1e293b 100%)`,
								filter: 'brightness(0.7)'
							}}
						/>

						{/* Content */}
						<div
							style={{
								position: 'relative',
								height: '100%',
								maxWidth: 1300,
								margin: '0 auto',
								padding: isMobile ? '20px' : '0 60px 40px',
								display: 'flex',
								alignItems: 'flex-end',
							}}
						>
							<div
								style={{
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'flex-end',
									width: '100%',
									flexWrap: 'wrap',
									gap: 24,
								}}
							>
								<div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
									<motion.div
										initial={{ scale: 0.9, opacity: 0 }}
										animate={{ scale: 1, opacity: 1 }}
										style={{
											width: isMobile ? 80 : 120,
											height: isMobile ? 80 : 120,
											borderRadius: 16,
											background: isDarkMode ? themeColors.bg : '#fff',
											padding: 12,
											boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											zIndex: 1
										}}
									>

										<img
											src={logoSrc}
											alt='Logo'
											style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
											onError={(e) => (e.currentTarget.src = themeFallbackLogo)}
										/>
									</motion.div>
									<div>
										<Title
											level={isMobile ? 3 : 1}
											style={{
												color: '#fff',
												margin: 0,
												fontWeight: 800,
												letterSpacing: '-0.02em',
												textShadow: '0 2px 4px rgba(0,0,0,0.3)'
											}}
										>
											{orgInfo.organizationName || 'Organization Profile'}
										</Title>
										{!isMobile && (
											<div style={{ marginTop: 4 }}>
												<Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
													<FileTextOutlined style={{ marginRight: 8 }} />
													{orgInfo.description || 'Description'}
												</Text>
											</div>
										)}
									</div>
								</div>

								<div style={{ paddingBottom: 10 }}>
									{hasOrganization ? (
										<Button
											type='primary'
											ghost={isDarkMode}
											icon={<EditOutlined />}
											onClick={handleOpenEditModal}
											style={{
												borderRadius: 6,
												height: 40,
												padding: '0 20px',
												backgroundColor: isDarkMode ? 'rgba(56, 189, 248, 0.08)' : undefined,
												fontWeight: isDarkMode ? 600 : undefined
											}}
										>
											Edit Profile
										</Button>
									) : (
										<Button
											type='primary'
											ghost={isDarkMode}
											icon={<PlusOutlined />}
											onClick={handleOpenCreateModal}
											style={{
												borderRadius: 6,
												height: 40,
												backgroundColor: isDarkMode ? 'rgba(56, 189, 248, 0.08)' : undefined,
												fontWeight: isDarkMode ? 600 : undefined
											}}
										>
											Configure
										</Button>
									)}
								</div>
							</div>
						</div>
					</div>

					{/* Main Grid */}
					<div
						style={{
							maxWidth: 1300,
							margin: '0 auto',
							padding: isMobile ? '24px 16px' : '40px 60px',
						}}
					>
						{!hasOrganization ? (
							<Card
								style={{ ...CARD_STYLE, textAlign: 'center', padding: '80px 0' }}
								bordered={false}
							>
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description="Configure your organization profile to get started."
								>
									<Button
										type='primary'
										icon={<PlusOutlined />}
										onClick={handleOpenCreateModal}
										style={{ marginTop: 24 }}
									>
										Start Setup
									</Button>
								</Empty>
							</Card>
						) : (
							<Row gutter={32}>
								{/* Summary Sidebar */}
								<Col xs={24} lg={6}>
									<Card bordered={false} style={{ ...CARD_STYLE, height: '100%' }} bodyStyle={{ padding: '24px' }}>
										<Title level={4} style={{ marginBottom: 24, color: themeColors.text }}>Summary</Title>

										<Space direction="vertical" size={24} style={{ width: '100%' }}>
											<div>
												<Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: themeColors.textTertiary }}>
													Operating Area
												</Text>
												<div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
													<EnvironmentOutlined style={{ color: primary, fontSize: 14 }} />
													<Text strong style={{ fontSize: 14, color: themeColors.text }}>
														{orgInfo.latitude?.toFixed(4)}, {orgInfo.longitude?.toFixed(4)}
													</Text>
												</div>
											</div>

											<div>
												<Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: themeColors.textTertiary }}>
													Check-in Type
												</Text>
												<div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
													<RadiusSettingOutlined style={{ color: primary, fontSize: 14 }} />
													<Text strong style={{ fontSize: 14, color: themeColors.text }}>
														Geofenced ({orgInfo.allowCheckInDistance}m)
													</Text>
												</div>
											</div>

											{orgInfo.websiteUrl && (
												<div>
													<Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: themeColors.textTertiary }}>
														Website
													</Text>
													<div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
														<GlobalOutlined style={{ color: primary, fontSize: 14 }} />
														<a href={orgInfo.websiteUrl} target="_blank" rel="noreferrer">
															<Text ellipsis style={{ color: primary, fontSize: 14 }}>
																{orgInfo.websiteUrl.replace(/^https?:\/\//, '')}
															</Text>
														</a>
													</div>
												</div>
											)}
										</Space>
									</Card>
								</Col>

								{/* Main Content */}
								<Col xs={24} lg={18}>
									<Card
										bordered={false}
										style={{ ...CARD_STYLE, minHeight: '100%' }}
										bodyStyle={{ padding: '0 24px 24px' }}
									>
										<Tabs
											activeKey={activeTab}
											onChange={setActiveTab}
											items={tabItems}
											size="large"
											className={isDarkMode ? 'dark-tabs' : ''}
										/>
									</Card>
								</Col>

							</Row>
						)}
					</div>
				</div>
			)}

			<OrganizationProfileUpdate
				isOpen={isModalOpen}
				mode={modalMode}
				orgInfo={orgInfo}
				onClose={handleCloseModal}
				onSuccess={fetchOrganization}
				onSubmit={handleSubmit}
				loading={loading}
			/>
		</>
	)
}

export default OrganizationProfile
